import { useCallback, useEffect, useRef, useState } from 'react';
import type { ListMemoryThreadsResponse } from '@mastra/client-js';
import { v7 as uuidv7 } from 'uuid';

import type {
  ConversationItem,
  ImageConversationItem,
} from '@/components/ai-image-assistant/types';
import { imageUploadSubject } from '@/lib/ai-asset-upload';
import { showAiCreditsExhaustedModal } from '@/lib/ai-credits-gate';
import { processImageCreatorStreamChunk } from '@/lib/assistant/image-creator-message-reducer';
import {
  findPendingImageConfirmation,
  type ImageGenerationConfirmation,
  imageGenerationAcknowledgement,
  readImageGenerationConfirmation,
} from '@/lib/assistant/image-generation-confirmation';
import { REQUEST_CANCELLED_TEXT } from '@/lib/assistant/message-turn';
import { extractContentText } from '@/lib/json-utils';
import { getChunkPayload } from '@/lib/mastra/stream';
import { RequestContext } from '@/lib/mastra/types';
import { readTrimmedString } from '@/lib/prop-utils';
import type { HistoryMessageRecord } from '@/lib/site-assistant/history-message-hydrator';
import { getLocalStorage, removeLocalStorage, setLocalStorage } from '@/lib/utils';
import { useAccountLimitsStore, useAiCreditStatus } from '@/stores/account-limits-store';
import type { CreditNotice, CreditStatus } from '@/types/shared';
import { useCreditNoticeStream } from './credits/use-credit-notice-stream';
import { useMastraAgent } from './use-mastra-agent';
import { useMastraClient } from './use-mastra-client';

export const IMAGE_CREATOR_AGENT_ID = 'imageAssistant';

const IMAGE_RESULT_PREFIX = 'IMAGE_RESULT:';
const IMAGE_FAILED_PREFIX = 'IMAGE_FAILED:';
const GENERATE_IMAGE_TOOL_NAME = 'generateImage';
const IMAGE_GENERATION_FAILED_TEXT = 'Image generation failed. Please try again.';
const OPEN_IMAGE_EDITOR_TOOL_NAME = 'openImageEditor';
const APPLY_IMAGE_TRANSFORM_TOOL_NAME = 'applyImageTransform';

export interface EditorAdjustments {
  directTransform?: 'upscale' | 'remove-bg';
}

type ImageCreatorThread = ListMemoryThreadsResponse['threads'][number];

interface UseImageCreatorAssistantOptions {
  userId: string;
  accountId: string | undefined;
  scopeKey?: string;
  clearStoredThreadOnUnmount?: boolean;
}

export interface UseImageCreatorAssistantReturn {
  conversation: ConversationItem[];
  threads: ImageCreatorThread[];
  isGenerating: boolean;
  isLoadingHistory: boolean;
  isLoadingThreads: boolean;
  creditStatus: CreditStatus | null;
  hasStoredThread: boolean;
  threadId: string;
  openEditorRequest: number;
  pendingEditorAdjustments: EditorAdjustments | null;
  sendMessage: (prompt: string, options?: { latestImageUrl?: string }) => Promise<void>;
  stop: () => void;
  refreshThreads: () => Promise<void>;
  createThread: () => string;
  selectThread: (nextThreadId: string) => void;
  updateImageInConversation: (sourceId: string, newUrl: string) => void;
  addEditedImageToConversation: (sourceId: string, newUrl: string) => string;
  removeTransformPlaceholder: () => void;
  saveEditedImageToThread: (
    permanentUrl: string,
    prompt?: string,
    rootPrompt?: string,
  ) => Promise<void>;
}

function extractImagePayloadFromToolContent(
  content: unknown,
): { imageUrl: string; prompt?: string; rootPrompt?: string } | null {
  if (typeof content === 'string') {
    try {
      return extractImagePayloadFromToolContent(JSON.parse(content));
    } catch {
      return null;
    }
  }
  if (Array.isArray(content)) {
    for (const part of content) {
      const payload = extractImagePayloadFromToolContent(part);
      if (payload) return payload;
    }
    return null;
  }
  if (content && typeof content === 'object') {
    const obj = content as Record<string, unknown>;
    if (typeof obj.imageUrl === 'string') {
      return {
        imageUrl: obj.imageUrl,
        prompt: readTrimmedString(obj.prompt),
        rootPrompt: readTrimmedString(obj.rootPrompt),
      };
    }
    if (obj.result) return extractImagePayloadFromToolContent(obj.result);
  }
  return null;
}

type GeneratedImageResult =
  | { imageUrl: string; prompt?: string; rootPrompt?: string; note?: string }
  | { confirmation: ImageGenerationConfirmation }
  | { error: string };

function extractGeneratedImageResults(content: unknown): GeneratedImageResult[] {
  if (!content || typeof content !== 'object') return [];
  const parts = (content as Record<string, unknown>).parts;
  if (!Array.isArray(parts)) return [];

  return parts.flatMap((part): GeneratedImageResult[] => {
    if (!part || typeof part !== 'object') return [];
    const invocation = (part as Record<string, unknown>).toolInvocation;
    if (!invocation || typeof invocation !== 'object') return [];
    const { toolName, state, result } = invocation as Record<string, unknown>;
    if (toolName !== GENERATE_IMAGE_TOOL_NAME) return [];
    if (state === 'output-error') return [{ error: IMAGE_GENERATION_FAILED_TEXT }];
    if (state !== 'result') return [];

    const confirmation = readImageGenerationConfirmation(result);
    if (confirmation) return [{ confirmation }];
    if (result && typeof result === 'object') {
      const resultRecord = result as Record<string, unknown>;
      if (resultRecord.success === false && typeof resultRecord.message === 'string') {
        return [{ error: resultRecord.message }];
      }
    }
    const payload = extractImagePayloadFromToolContent(result);
    const note =
      result && typeof result === 'object' && 'note' in result && typeof result.note === 'string'
        ? result.note
        : undefined;
    return payload ? [{ ...payload, note }] : [];
  });
}

function parseImageResultMessage(
  value: string,
  fallbackPrompt: string,
): { imageUrl: string; prompt: string; rootPrompt: string } | null {
  try {
    const parsed = JSON.parse(value) as Record<string, unknown>;
    if (typeof parsed.imageUrl === 'string') {
      const prompt = readTrimmedString(parsed.prompt) ?? fallbackPrompt;
      return {
        imageUrl: parsed.imageUrl,
        prompt,
        rootPrompt: readTrimmedString(parsed.rootPrompt) ?? prompt,
      };
    }
  } catch {
    return value ? { imageUrl: value, prompt: fallbackPrompt, rootPrompt: fallbackPrompt } : null;
  }

  return null;
}

function extractCreditNotice(content: unknown): CreditNotice | null {
  if (!content || typeof content !== 'object') return null;
  const parts = (content as Record<string, unknown>).parts;
  if (!Array.isArray(parts)) return null;

  const noticePart = parts.find(
    (part) =>
      part &&
      typeof part === 'object' &&
      (part as Record<string, unknown>).type === 'data-creditNotice',
  ) as Record<string, unknown> | undefined;
  const data = noticePart?.data;
  if (!data || typeof data !== 'object') return null;

  const notice = data as Partial<CreditNotice>;
  if (
    typeof notice.noticeId !== 'string' ||
    typeof notice.threshold !== 'number' ||
    typeof notice.percentUsed !== 'number' ||
    typeof notice.remaining !== 'number' ||
    typeof notice.crossedAt !== 'string' ||
    !notice.level
  ) {
    return null;
  }

  return notice as CreditNotice;
}

function hydrateHistory(messages: HistoryMessageRecord[]): ConversationItem[] {
  const items: ConversationItem[] = [];
  let lastUserPrompt = '';

  const addOrUpdateImage = (image: ImageConversationItem) => {
    const lastItem = items[items.length - 1];
    if (lastItem?.type === 'image' && lastItem.imageUrl === image.imageUrl) {
      items[items.length - 1] = {
        ...lastItem,
        prompt: image.prompt || lastItem.prompt,
        rootPrompt:
          readTrimmedString(image.rootPrompt) ??
          readTrimmedString(lastItem.rootPrompt) ??
          imageUploadSubject(lastItem),
      };
      return;
    }

    items.push(image);
  };

  for (const msg of messages) {
    const creditNotice = extractCreditNotice(msg.content);
    if (creditNotice) {
      items.push({ id: uuidv7(), type: 'credit_notice', creditNotice });
      continue;
    }

    if (msg.role === 'user') {
      const text = extractContentText(msg.content, { partSeparator: ' ', trimText: true });
      lastUserPrompt = text;
      if (items[items.length - 1]?.type === 'user') {
        items.push({ id: uuidv7(), type: 'assistant', text: REQUEST_CANCELLED_TEXT });
      }
      items.push({ id: uuidv7(), type: 'user', text });
    } else if ((msg.role as string) === 'tool') {
      const confirmation = readImageGenerationConfirmation(msg.content);
      if (confirmation) {
        items.push({
          id: uuidv7(),
          type: 'assistant',
          text: confirmation.message,
          imageConfirmation: confirmation,
        });
        continue;
      }
      const imagePayload = extractImagePayloadFromToolContent(msg.content);
      if (imagePayload) {
        const prompt = readTrimmedString(imagePayload.prompt) ?? lastUserPrompt;
        addOrUpdateImage({
          id: uuidv7(),
          type: 'image',
          imageUrl: imagePayload.imageUrl,
          prompt,
          rootPrompt: readTrimmedString(imagePayload.rootPrompt) ?? prompt,
        });
      }
    } else if (msg.role === 'assistant') {
      const generatedResults = extractGeneratedImageResults(msg.content);
      for (const result of generatedResults) {
        if ('confirmation' in result) {
          items.push({
            id: uuidv7(),
            type: 'assistant',
            text: result.confirmation.message,
            imageConfirmation: result.confirmation,
          });
          continue;
        }
        if ('error' in result) {
          items.push({ id: uuidv7(), type: 'error', text: result.error, prompt: lastUserPrompt });
          continue;
        }
        const prompt = readTrimmedString(result.prompt) ?? lastUserPrompt;
        addOrUpdateImage({
          id: uuidv7(),
          type: 'image',
          imageUrl: result.imageUrl,
          prompt,
          rootPrompt: readTrimmedString(result.rootPrompt) ?? prompt,
        });
        if (result.note)
          items.push({ id: uuidv7(), type: 'assistant', text: result.note, imagePolicyNote: true });
      }
      const hasGeneratedError = generatedResults.some((result) => 'error' in result);

      const text = extractContentText(msg.content, { partSeparator: ' ', trimText: true });
      if (text.startsWith(IMAGE_RESULT_PREFIX)) {
        const result = parseImageResultMessage(
          text.slice(IMAGE_RESULT_PREFIX.length).trim(),
          lastUserPrompt,
        );
        if (result) {
          addOrUpdateImage({
            id: uuidv7(),
            type: 'image',
            imageUrl: result.imageUrl,
            prompt: result.prompt,
            rootPrompt: result.rootPrompt ?? result.prompt,
          });
        }
      } else if (text.startsWith(IMAGE_FAILED_PREFIX)) {
        if (hasGeneratedError) continue;
        const errorText = text.slice(IMAGE_FAILED_PREFIX.length).trim();
        items.push({ id: uuidv7(), type: 'error', text: errorText, prompt: lastUserPrompt });
      } else if (text && !generatedResults.some((result) => 'confirmation' in result)) {
        const cleanText = text.replace(/\s*\{"imageUrl"\s*:\s*"[^"]*"\}\s*$/, '').trim();
        if (cleanText) {
          items.push({ id: uuidv7(), type: 'assistant', text: cleanText });
        }
      }
    }
  }

  const lastItem = items[items.length - 1];
  if (lastItem?.type === 'user') {
    items.push({ id: uuidv7(), type: 'assistant', text: REQUEST_CANCELLED_TEXT });
  }

  return items;
}

function findSourceImageRootPrompt(
  items: ConversationItem[],
  latestImageUrl: string,
): string | undefined {
  const sourceImage = [...items]
    .reverse()
    .find(
      (item): item is ImageConversationItem =>
        item.type === 'image' && item.imageUrl === latestImageUrl,
    );
  return sourceImage ? imageUploadSubject(sourceImage) : undefined;
}

export function useImageCreatorAssistant({
  userId,
  accountId: rawAccountId,
  scopeKey,
  clearStoredThreadOnUnmount = true,
}: UseImageCreatorAssistantOptions): UseImageCreatorAssistantReturn {
  const accountId = rawAccountId ?? '';
  const storageKey = scopeKey
    ? `image-creator-thread-${scopeKey}-${userId}-${accountId}`
    : `image-creator-thread-${userId}-${accountId}`;

  const resourceId = scopeKey
    ? `${scopeKey}:${userId}:${accountId}:imageAssistant`
    : `${userId}:${accountId}:imageAssistant`;

  const creditStatus = useAiCreditStatus();
  const setAiCreditStatus = useAccountLimitsStore((state) => state.setAiCreditStatus);

  const updateCreditStatus = useCallback(
    (nextCreditStatus: CreditStatus) => {
      setAiCreditStatus(nextCreditStatus);
    },
    [setAiCreditStatus],
  );
  const mastraClient = useMastraClient({ onCreditStatus: updateCreditStatus });
  const knownThreadIdsRef = useRef<Set<string>>(new Set());
  const hasLoadedHistoryRef = useRef(false);
  const historyLoadPromiseRef = useRef<Promise<void> | null>(null);
  const isSendingRef = useRef(false);
  const stopRequestedRef = useRef(false);
  const pendingThreadIdRef = useRef<string | null>(null);

  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(() =>
    Boolean(getLocalStorage(storageKey)),
  );
  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [conversation, setConversation] = useState<ConversationItem[]>([]);
  const conversationRef = useRef<ConversationItem[]>([]);
  conversationRef.current = conversation;
  const [threads, setThreads] = useState<ImageCreatorThread[]>([]);
  const [hasStoredThread, setHasStoredThread] = useState<boolean>(() =>
    Boolean(getLocalStorage(storageKey)),
  );
  const [editorRequest, setEditorRequest] = useState<{
    seq: number;
    adjustments: EditorAdjustments | null;
  }>({ seq: 0, adjustments: null });

  const [discoveryState, setDiscoveryState] = useState<'needed' | 'done'>('needed');

  const [threadId, setThreadId] = useState<string>(() => {
    const stored = getLocalStorage(storageKey);
    if (stored) return stored;
    const id = uuidv7();
    pendingThreadIdRef.current = id;
    return id;
  });
  const activeThreadIdRef = useRef(threadId);
  activeThreadIdRef.current = threadId;

  const threadScopedMastraClient = useMastraClient({
    threadId,
    onCreditStatus: updateCreditStatus,
  });
  const {
    isStreaming: isGenerating,
    sendMessage: sendAgentMessage,
    stop,
  } = useMastraAgent({
    agentId: IMAGE_CREATOR_AGENT_ID,
    mastraClient: threadScopedMastraClient,
  });

  const showCreditNotice = useCallback((notice: CreditNotice) => {
    setConversation((prev) => [
      ...prev,
      { id: uuidv7(), type: 'credit_notice', creditNotice: notice },
    ]);
  }, []);

  const { resetRenderedNotices, markNoticeRendered } = useCreditNoticeStream({
    agentId: IMAGE_CREATOR_AGENT_ID,
    threadId,
    resourceId,
    mastraClient: threadScopedMastraClient,
    isBusy: isGenerating,
    onCreditStatus: updateCreditStatus,
    onShow: showCreditNotice,
  });

  const setActiveThread = useCallback(
    (nextId: string) => {
      pendingThreadIdRef.current = nextId;
      setLocalStorage(storageKey, nextId);
      setHasStoredThread(true);
      setThreadId(nextId);
      setDiscoveryState('done');
    },
    [storageKey],
  );

  useEffect(() => {
    const stored = getLocalStorage(storageKey);
    setHasStoredThread(Boolean(stored));
    if (stored) {
      pendingThreadIdRef.current = null;
      setThreadId(stored);
    } else {
      const id = uuidv7();
      pendingThreadIdRef.current = id;
      setThreadId(id);
    }
    setDiscoveryState('needed');
  }, [storageKey]);

  useEffect(() => {
    if (!clearStoredThreadOnUnmount) return;
    return () => {
      removeLocalStorage(storageKey);
    };
  }, [clearStoredThreadOnUnmount, storageKey]);

  useEffect(() => {
    hasLoadedHistoryRef.current = false;
    historyLoadPromiseRef.current = null;
    resetRenderedNotices();
    setConversation([]);
    setIsLoadingHistory(getLocalStorage(storageKey) === threadId);
  }, [threadId, storageKey, resetRenderedNotices]);

  useEffect(() => {
    if (discoveryState !== 'needed') return;

    const discover = async () => {
      try {
        const result = await mastraClient.listMemoryThreads({
          resourceId,
          agentId: IMAGE_CREATOR_AGENT_ID,
        });
        const fetchedThreads = result.threads as ImageCreatorThread[];
        setThreads(fetchedThreads);
        knownThreadIdsRef.current = new Set(fetchedThreads.map((t) => t.id));
      } catch {
        // Discovery failed — proceed with the generated UUID.
      }
      const pending = pendingThreadIdRef.current;
      if (pending) {
        setLocalStorage(storageKey, pending);
        setHasStoredThread(true);
      }
      setDiscoveryState('done');
    };

    void discover();
  }, [discoveryState, storageKey, resourceId, mastraClient]);

  useEffect(() => {
    if (!threadId || hasLoadedHistoryRef.current || discoveryState !== 'done') return;

    if (!knownThreadIdsRef.current.has(threadId)) {
      hasLoadedHistoryRef.current = true;
      setIsLoadingHistory(false);
      return;
    }

    let isActive = true;
    const loadHistory = async () => {
      hasLoadedHistoryRef.current = true;
      setIsLoadingHistory(true);
      try {
        const thread = threadScopedMastraClient.getMemoryThread({
          threadId,
          agentId: IMAGE_CREATOR_AGENT_ID,
        });
        const result = await thread.listMessages({ page: 0, perPage: 50 });
        if (!isActive) return;
        const hydrated = hydrateHistory(result.messages as HistoryMessageRecord[]);
        hydrated.forEach((item) => {
          if (item.type === 'credit_notice') {
            markNoticeRendered(item.creditNotice.noticeId);
          }
        });
        setConversation(hydrated);
      } catch {
        // Thread doesn't exist yet — first message will create it.
      } finally {
        if (isActive) {
          setIsLoadingHistory(false);
        }
      }
    };

    const historyLoadPromise = loadHistory();
    historyLoadPromiseRef.current = historyLoadPromise;
    void historyLoadPromise.finally(() => {
      if (historyLoadPromiseRef.current === historyLoadPromise) {
        historyLoadPromiseRef.current = null;
      }
    });

    return () => {
      isActive = false;
    };
  }, [threadId, threadScopedMastraClient, discoveryState, markNoticeRendered]);

  const refreshThreads = useCallback(async () => {
    setIsLoadingThreads(true);
    try {
      const result = await mastraClient.listMemoryThreads({
        resourceId,
        agentId: IMAGE_CREATOR_AGENT_ID,
      });
      setThreads(result.threads as ImageCreatorThread[]);
      knownThreadIdsRef.current = new Set(result.threads.map((t) => t.id));
    } catch {
      // Keep current thread list on failure.
    } finally {
      setIsLoadingThreads(false);
    }
  }, [mastraClient, resourceId]);

  useEffect(() => {
    void refreshThreads();
  }, [refreshThreads]);

  const createThread = useCallback(() => {
    const newId = uuidv7();
    setActiveThread(newId);
    setConversation([]);
    return newId;
  }, [setActiveThread]);

  const selectThread = useCallback(
    (nextThreadId: string) => {
      if (nextThreadId === threadId) return;
      setActiveThread(nextThreadId);
      setConversation([]);
    },
    [setActiveThread, threadId],
  );

  const ensureThreadExists = useCallback(async () => {
    if (knownThreadIdsRef.current.has(threadId)) return;
    try {
      const thread = await mastraClient.createMemoryThread({
        resourceId,
        agentId: IMAGE_CREATOR_AGENT_ID,
        threadId,
        metadata: { userId, accountId, type: IMAGE_CREATOR_AGENT_ID },
      });
      knownThreadIdsRef.current.add(thread.id);
    } catch {
      // Transient create failures must not mark the id known — retry on next send.
    }
  }, [threadId, resourceId, userId, accountId, mastraClient]);

  const sendMessage = useCallback(
    async (promptText: string, options?: { latestImageUrl?: string }): Promise<void> => {
      const trimmed = promptText.trim();
      if (!trimmed || isGenerating || isSendingRef.current) return;
      if (creditStatus?.level === 'exhausted') {
        showAiCreditsExhaustedModal();
        return;
      }

      isSendingRef.current = true;
      stopRequestedRef.current = false;
      const requestThreadId = threadId;
      const generatingId = uuidv7();
      let hasOptimisticTurn = false;
      let pendingTransform: 'upscale' | 'remove-bg' | null = null;
      let rootPrompt = trimmed;

      try {
        await historyLoadPromiseRef.current?.catch(() => undefined);

        if (stopRequestedRef.current || activeThreadIdRef.current !== requestThreadId) return;

        const pendingImageConfirmation = findPendingImageConfirmation(conversationRef.current);
        hasOptimisticTurn = true;
        setConversation((prev) => [
          ...prev,
          { id: uuidv7(), type: 'user', text: trimmed },
          { id: generatingId, type: 'thinking', prompt: trimmed },
        ]);

        await ensureThreadExists();

        const acknowledgement = imageGenerationAcknowledgement(pendingImageConfirmation, trimmed);
        const requestContext = new RequestContext();
        if (acknowledgement) requestContext.set('imageGenerationAcknowledgement', acknowledgement);
        requestContext.set('userId', userId);
        requestContext.set('accountId', accountId ?? '');
        if (options?.latestImageUrl) {
          requestContext.set('latestImageUrl', options.latestImageUrl);
          const inheritedRootPrompt = findSourceImageRootPrompt(
            conversationRef.current,
            options.latestImageUrl,
          );
          if (inheritedRootPrompt) {
            rootPrompt = inheritedRootPrompt;
          }
        }

        let openEditorCalled = false;

        await sendAgentMessage(trimmed, {
          concurrency: 'reject',
          streamOptions: {
            memory: {
              thread: {
                id: threadId,
                metadata: { userId, accountId, type: IMAGE_CREATOR_AGENT_ID },
              },
              resource: resourceId,
            },
            requestContext,
            maxSteps: 3,
          },
          onChunk: (chunk) => {
            if (chunk.type === 'tool-call') {
              const chunkPayload = getChunkPayload(chunk);
              const toolName = chunkPayload.toolName;
              if (toolName === GENERATE_IMAGE_TOOL_NAME) {
                const args = chunkPayload.args as { sourceImageUrl?: unknown } | undefined;
                const sourceImageUrl =
                  typeof args?.sourceImageUrl === 'string' ? args.sourceImageUrl : undefined;
                rootPrompt = sourceImageUrl
                  ? (findSourceImageRootPrompt(conversationRef.current, sourceImageUrl) ?? trimmed)
                  : trimmed;
              } else if (toolName === OPEN_IMAGE_EDITOR_TOOL_NAME) {
                openEditorCalled = true;
              } else if (toolName === APPLY_IMAGE_TRANSFORM_TOOL_NAME) {
                const args = chunkPayload.args as
                  | { transform?: 'upscale' | 'remove-bg' }
                  | undefined;
                if (args?.transform) pendingTransform = args.transform;
              }
            }
            setConversation((prev) =>
              processImageCreatorStreamChunk(prev, chunk, { prompt: trimmed, rootPrompt }),
            );
          },
        });

        if (openEditorCalled || pendingTransform) {
          setEditorRequest((prev) => ({
            seq: prev.seq + 1,
            adjustments: pendingTransform ? { directTransform: pendingTransform } : null,
          }));
        }
      } catch (err) {
        if (
          hasOptimisticTurn &&
          !(err instanceof Error && err.name === 'AbortError') &&
          !stopRequestedRef.current
        ) {
          setConversation((prev) =>
            prev.map((item) =>
              item.id === generatingId
                ? {
                    id: generatingId,
                    type: 'error' as const,
                    text: err instanceof Error ? err.message : IMAGE_GENERATION_FAILED_TEXT,
                    prompt: trimmed,
                  }
                : item,
            ),
          );
        }
      } finally {
        if (hasOptimisticTurn) {
          setConversation((prev) => {
            if (stopRequestedRef.current) {
              return prev.map((item) =>
                item.id === generatingId
                  ? { id: generatingId, type: 'assistant' as const, text: REQUEST_CANCELLED_TEXT }
                  : item,
              );
            }
            if (pendingTransform) return prev;
            return prev.map((item) =>
              item.id === generatingId && (item.type === 'thinking' || item.type === 'generating')
                ? {
                    id: generatingId,
                    type: 'error' as const,
                    text: IMAGE_GENERATION_FAILED_TEXT,
                    prompt: trimmed,
                  }
                : item,
            );
          });
        }
        isSendingRef.current = false;
      }
    },
    [
      isGenerating,
      creditStatus?.level,
      ensureThreadExists,
      userId,
      accountId,
      threadId,
      resourceId,
      sendAgentMessage,
    ],
  );

  const updateImageInConversation = useCallback((sourceId: string, newUrl: string) => {
    setConversation((prev) =>
      prev.map((item) =>
        item.id === sourceId && item.type === 'image' ? { ...item, imageUrl: newUrl } : item,
      ),
    );
  }, []);

  const addEditedImageToConversation = useCallback((sourceId: string, newUrl: string): string => {
    const newId = uuidv7();
    setConversation((prev) => {
      const sourceItem = prev.find((item) => item.id === sourceId);
      const prompt = sourceItem?.type === 'image' ? sourceItem.prompt : '';
      const rootPrompt = sourceItem?.type === 'image' ? imageUploadSubject(sourceItem) : undefined;
      const filtered = prev.filter((item) => item.type !== 'generating');
      return [
        ...filtered,
        {
          id: newId,
          type: 'image' as const,
          imageUrl: newUrl,
          prompt,
          ...(rootPrompt ? { rootPrompt } : {}),
        },
      ];
    });
    return newId;
  }, []);

  const removeTransformPlaceholder = useCallback(() => {
    setConversation((prev) => prev.filter((item) => item.type !== 'generating'));
  }, []);

  const saveEditedImageToThread = useCallback(
    async (permanentUrl: string, prompt?: string, rootPrompt?: string): Promise<void> => {
      try {
        const payload = {
          imageUrl: permanentUrl,
          ...(prompt ? { prompt } : {}),
          ...(rootPrompt ? { rootPrompt } : {}),
        };
        await mastraClient.saveMessageToMemory({
          agentId: IMAGE_CREATOR_AGENT_ID,
          messages: [
            {
              id: uuidv7(),
              role: 'assistant',
              type: 'text',
              content: `${IMAGE_RESULT_PREFIX}${JSON.stringify(payload)}`,
              threadId,
              resourceId,
              createdAt: new Date(),
            },
          ],
        });
      } catch (err) {
        console.warn('[ImageCreatorAssistant] Failed to persist edited image to memory:', err);
      }
    },
    [mastraClient, threadId, resourceId],
  );

  return {
    conversation,
    threads,
    isGenerating,
    isLoadingHistory,
    isLoadingThreads,
    creditStatus,
    hasStoredThread,
    threadId,
    openEditorRequest: editorRequest.seq,
    pendingEditorAdjustments: editorRequest.adjustments,
    sendMessage,
    stop: useCallback(() => {
      stopRequestedRef.current = true;
      stop();
    }, [stop]),
    refreshThreads,
    createThread,
    selectThread,
    updateImageInConversation,
    addEditedImageToConversation,
    removeTransformPlaceholder,
    saveEditedImageToThread,
  };
}
